import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { MapPin, CalendarDays, Clock, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LoadingScreen } from "@/components/LoadingScreen";
import crown from "@/assets/crown.png";

const Cta = ({ onClick, label }) => (
  <Button
    onClick={onClick}
    data-testid="plan-preview-cta"
    className="bg-[#D4AF37] text-[#0B0B0F] hover:bg-[#F1D570] font-semibold rounded-md px-8 h-12 text-base transition-colors"
  >
    {label}
    <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
  </Button>
);

export default function PlanPreview() {
  const { id } = useParams();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    api
      .get(`/plans/${id}/preview`)
      .then((r) => {
        setPlan(r.data);
        setState("ok");
      })
      .catch(() => setState("notfound"));
  }, [id]);

  useEffect(() => {
    if (plan?.title) document.title = `THE CIRCLE — ${plan.title}`;
    return () => {
      document.title = "THE CIRCLE";
    };
  }, [plan]);

  const cta = () => (user ? navigate("/circle") : login());

  if (state === "loading") return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-white" data-testid="plan-preview-page">
      <header className="sticky top-0 z-40 bg-[#0B0B0F]/80 backdrop-blur-xl border-b border-[#D4AF37]/20">
        <div className="max-w-2xl mx-auto px-6 h-16 flex items-center justify-center">
          <Link to="/" className="flex items-center gap-2.5" data-testid="plan-preview-brand">
            <img src={crown} alt="" className="w-7 h-7" />
            <span className="font-serif font-bold text-base tracking-wide text-[#D4AF37]">THE CIRCLE</span>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-20 flex flex-col items-center text-center">
        <img src={crown} alt="" className="w-16 h-16 crown-glow mb-8 fade-up" />

        {state === "notfound" ? (
          <div className="fade-up" data-testid="plan-preview-notfound">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              This table could not be found
            </h1>
            <p className="text-[#A0A0A0] text-base mb-10">
              The link may be incorrect, or the plan was removed.
            </p>
            <Cta onClick={cta} label="Explore THE CIRCLE" />
          </div>
        ) : plan.status !== "open" ? (
          <div className="fade-up" data-testid="plan-preview-closed">
            <p className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] mb-4">The seats are taken</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              This plan is no longer open
            </h1>
            <p className="text-[#A0A0A0] text-base mb-10">
              Explore other sponsored tables on THE CIRCLE.
            </p>
            <Cta onClick={cta} label="Sign in to Explore Plans" />
          </div>
        ) : (
          <div className="fade-up w-full flex flex-col items-center">
            <p className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] mb-4">You're invited</p>
            <h1
              className="font-serif text-3xl sm:text-4xl font-bold tracking-tight mb-6"
              data-testid="plan-preview-title"
            >
              {plan.title}
            </h1>
            <div className="gold-divider w-24 mb-8" />
            <div className="space-y-3 text-[#E0E0E0] text-base mb-10">
              <p className="flex items-center justify-center gap-2" data-testid="plan-preview-neighborhood">
                <MapPin className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                {plan.neighborhood}
              </p>
              <p className="flex items-center justify-center gap-2" data-testid="plan-preview-datetime">
                <CalendarDays className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                {format(new Date(plan.datetime), "EEE, d MMM yyyy")}
                <Clock className="w-4 h-4 text-[#D4AF37] ml-2" strokeWidth={1.5} />
                {format(new Date(plan.datetime), "h:mm a")}
              </p>
            </div>
            <Cta onClick={cta} label="Sign in to Join This Plan" />
            <p className="text-xs text-[#A0A0A0] mt-6">
              A planner sponsors the meal. You bring the company.{" "}
              <Link to="/how-it-works" className="text-[#D4AF37] underline underline-offset-4">
                How it works
              </Link>
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
